/** @jsxImportSource @emotion/react */
import React, { FC, useEffect } from 'react';
import { FormikErrors } from 'formik';
import { Selectable, TextFields } from '@zip/business-components';
import { useMerchantApplicationContext } from 'contexts/MerchantApplication.context';
import {
  PaymentProviderProps,
  StepComponentProps,
} from 'types/interfaces/sign-up/MerchantApplication.types';
import { logEvent, Validators } from 'utils';
import { StepIndex } from '../signUpConfig';
import * as styles from './SignUpForm.styles';
import {
  SignUpRadioStepConfig,
  SignUpRadioStepForm,
  useSignUpRadioStep,
} from './useSignUpRadioStep.module';
import { PageAction } from 'enums';

const OTHER_OPTION = 'Other';
const OTHER_PROVIDER_MAX_LENGTH = 50;

const validateOtherProvider = (
  values: PaymentProviderProps
): FormikErrors<PaymentProviderProps> => {
  const errors: FormikErrors<PaymentProviderProps> = {};

  if (values.provider !== OTHER_OPTION) {
    return errors;
  }

  if (!values.otherProvider?.trim()) {
    errors.otherProvider = 'Please specify your payment provider';
  } else if (values.otherProvider.length > OTHER_PROVIDER_MAX_LENGTH) {
    errors.otherProvider = `Must be ${OTHER_PROVIDER_MAX_LENGTH} characters or less`;
  }

  return errors;
};

const config: SignUpRadioStepConfig<PaymentProviderProps> = {
  stepIndex: StepIndex.PaymentProvider,
  selectionField: 'provider',
  requiredSelectionMessage: 'Please select an option',
  initialValues: {
    provider: '',
    otherProvider: '',
  },
  validate: validateOtherProvider,
};

export const PaymentProviderStepModule: FC<StepComponentProps> = ({
  provideSubmit,
  serverErrors,
}) => {
  const { formOptions, merchantApplicationId } =
    useMerchantApplicationContext();
  const options: Selectable[] = (formOptions.PaymentProvider ?? []).map(
    (answer) => ({ label: answer, value: answer })
  );
  const step = useSignUpRadioStep(config, options, {
    provideSubmit,
    serverErrors,
  });
  const isOtherSelected = step.formik.values.provider === OTHER_OPTION;

  useEffect(() => {
    logEvent(PageAction.merchantOnboardingPVPaymentProvider, {
      merchantApplicationId,
    });
  }, []);

  useEffect(() => {
    if (!isOtherSelected && step.formik.values.otherProvider) {
      step.formik.setFieldValue('otherProvider', '');
    }
  }, [isOtherSelected, step.formik]);

  return (
    <>
      <SignUpRadioStepForm {...step} />
      {isOtherSelected && (
        <TextFields.Outlined
          type="text"
          name="otherProvider"
          label="Please specify your payment provider"
          css={[styles.textfield, styles.textfieldNoMargin]}
          fullWidth
          value={step.formik.values.otherProvider ?? ''}
          onChange={step.formik.handleChange}
          onBlur={step.formik.handleBlur}
          inputProps={{ maxLength: OTHER_PROVIDER_MAX_LENGTH }}
          error={
            step.submitAttempted && Boolean(step.formik.errors.otherProvider)
          }
          helperText={Validators.renderFieldError(
            step.formik.touched.otherProvider,
            step.formik.errors.otherProvider,
            step.submitAttempted
          )}
        />
      )}
    </>
  );
};

export default PaymentProviderStepModule;
